import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { log } from '../utils/DebugLogger'
import type { Logs_BuscarLogsRow } from '../contracts/Logs_BuscarLogsRow'
import '../contracts/Logs_BuscarLogsResponse'

/**
 * useLogs — network owner for the application activity log (`/logs`).
 *
 * fetchLogs() — GET /index.php/logs/buscar_logs.
 *   `dados` is `{ logs: [...] }` per Logs_BuscarLogsResponse. The array is
 *   unwrapped HERE: DataTable's endpoint loader assumes `dados` IS the row
 *   array and throws `data.map is not a function` on every load (same defect
 *   as webhook-logs; see useWebhookLogs).
 *
 * _reset() — wipes module-scope state; call in afterEach for test isolation.
 */

const logs = ref<Logs_BuscarLogsRow[]>([])
const loading = ref(false)
const loadError = ref<string | null>(null)
/** Quantas linhas a janela filtrada tem — pode ser maior que `logs.length`. */
const total = ref(0)
/** true quando o servidor cortou a listagem e a tela mostra um recorte. */
const truncated = ref(false)

const LIST_PATH = '/index.php/logs/buscar_logs'

export function useLogs() {
  async function fetchLogs(): Promise<Logs_BuscarLogsRow[]> {
    loading.value = true
    loadError.value = null
    try {
      const resp = await httpService.get(LIST_PATH, {
        logContext: 'useLogs',
        responseContract: 'Logs_BuscarLogsResponse',
      })
      logs.value = resp.dados?.logs ?? []
      total.value = resp.dados?.total ?? logs.value.length
      truncated.value = resp.dados?.truncated ?? false
      log('useLogs', 'fetchLogs', 'Logs carregados', {
        count: logs.value.length,
        total: total.value,
        truncated: truncated.value,
      })
      return logs.value
    } catch (err) {
      log('useLogs', 'fetchLogs', 'Erro ao carregar logs', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      logs.value = []
      total.value = 0
      truncated.value = false
      loadError.value = (err as Error)?.message ?? 'Erro desconhecido'
      throw err
    } finally {
      loading.value = false
    }
  }

  function _reset(): void {
    logs.value = []
    total.value = 0
    truncated.value = false
    loading.value = false
    loadError.value = null
  }

  return {
    logs,
    total,
    truncated,
    loading,
    loadError,
    fetchLogs,
    _reset,
  }
}
